import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../components/Button';
import { formatDuration } from '../../service/MicService';
import { uploadAudioFile } from '../../api/authApis';
import { RootState } from '../../redux/store';
import { getButtonSize, getWorkletURL } from '../../service/utils';
import { Text } from '../../components/Text';
import useComputedStyle from '../../hooks/useComputedStyle';
import classes from './MicrophoneSelection.module.css';
import { MicObject, ScreenButtonSize } from '../../interface/interface';
import { pushFaroEvent } from '../../faroSession';
import {
    changeFileSpeed,
    changeSpeakPrecheckDuration,
    changePageDetails,
    changeIsSilent,
    changePrecheckApiError,
} from '../../redux/appConfigSlice';
import { LiveAudioVisualizerV2 } from '../../components/LiveAudioVisualizerV2';
import { useOnnx } from '../../hooks/useAudioRecorder/subHooks/useOnnx';
import { encodeToWav, floatToPCM16, mergeChunks } from '../../service/AudioService';
import { apiUrl } from '../../constants/constants';

interface SpeakCheckScreenProps {
    selectedMic?: MicObject;
    silenceMeanValue: number;
    setIsSpeakCheckFinished: (val: boolean) => void;
    downLoadSpeed?: string;
    rules: ISpeakCheckRules;
    fontSize: ScreenButtonSize;
    workerRef: React.MutableRefObject<Worker | null>;
    ready: boolean;
}

export interface ISpeakCheckRules {
    silenceBeforeRetry: number;
    maxSpeechTime: number;
    silenceRequiredForComplete: number;
    samplePhrase: string;
}

const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;

const SpeakCheckScreen = (props: SpeakCheckScreenProps) => {
    const dispatch = useDispatch();
    const { elementRef, computedStyles } = useComputedStyle();

    const { testTaker: testTakerDetails, pageObject } = useSelector((state: RootState) => state.appConfigSlice);
    const insperaDetails = useSelector((state: RootState) => state.appConfigSlice.insperaDetails);
    const { rules, fontSize, selectedMic } = props;

    const [isRecordingStarted, setIsRecordingStarted] = useState(false);
    const [errorScreen, setErrorScreen] = useState(false);
    const [uploadingAudio, setUploadingAudio] = useState<boolean>(false);
    const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
    const [intentReceived, setIntentReceived] = useState<boolean>(false);
    const recordingTime = useRef<number>(0);
    const startTime = useRef<number>(0);
    const wsRef = useRef<WebSocket | null>(null);
    const audioContext = useRef<AudioContext | null>(null);
    const stream = useRef<MediaStream | null>(null);
    const micWorkletNode = useRef<AudioWorkletNode | null>(null);
    const recordedChunks = useRef<Float32Array[]>([]);
    const silentTimer = useRef<NodeJS.Timeout | null>(null);
    const speakTimer = useRef<NodeJS.Timeout | null>(null);
    const preCheckStartTimestampRef = useRef<string>();
    const preCheckEndTimestampRef = useRef<string>();
    const { workerRef, ready } = props;
    const { resetSileroStates } = useOnnx();
    

    useEffect(() => {
        dispatch(changeIsSilent(errorScreen));
        if (errorScreen) {
            pushFaroEvent('speak_check_failed', { reason: 'silence_detected_too_long' });
        }
    }, [errorScreen]);

    useEffect(() => {
        const ws = new WebSocket(
            `${WS_URL}${apiUrl.SPEECH_MATCHING}?cmsSessionId=${testTakerDetails.testTakerSessionId}`,
        );
        wsRef.current = ws;

        ws.onopen = () => {
            pushFaroEvent('WebSocket connection established for speech matching');
        };

        ws.onmessage = (event: any) => {
            try {
                const data = JSON.parse(event.data);
                if (data.intent === 'MATCH') {
                    setUploadingAudio(true);
                    onStop(true);
                    setIntentReceived(true);
                } else {
                    onStop();
                    setErrorScreen(true);
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        ws.onerror = () => console.error('WebSocket error occurred');
        ws.onclose = () => pushFaroEvent('WebSocket connection closed');

        return () => {
            // Clean up, Disconnect Websocket
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ action: 'stopTranscription' }));
                ws.close();
            }

            resetSileroStates();
        };
    }, []);

    useEffect(() => {
        if (isSpeaking) {
            silentTimer.current && clearTimeout(silentTimer.current);
        }
    }, [isSpeaking]);

    useEffect(() => {
        if (intentReceived) {
            speakTimer.current && clearTimeout(speakTimer.current);
        }
    }, [intentReceived])

    const onStart = useCallback(async () => {
        pushFaroEvent('speak_check_started');
        setIsRecordingStarted(true);
        startTime.current = Date.now();

        speakTimer.current = setTimeout(() => {
            if (intentReceived) return;

            setErrorScreen(true);
            onStop();
            console.log(
                `Maximum speech time of ${rules.maxSpeechTime} seconds exceeded. Showing error screen.`,
            );
        }, rules.maxSpeechTime * 1000);

        preCheckStartTimestampRef.current = new Date().toISOString();
        
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'startTranscription' }));
        }

        silentTimer.current = setTimeout(
            () => {
                setErrorScreen(true);
                onStop();
                console.log(
                    `No voice activity detected for ${rules.silenceBeforeRetry} seconds. Showing error screen.`,
                );
            },
            (rules.silenceBeforeRetry ?? 8) * 1000,
        );

        try {
            audioContext.current = new AudioContext({ sampleRate: 16000 });
            stream.current = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: { exact: selectedMic?.deviceId ?? 'default' },
                    noiseSuppression: true,
                    echoCancellation: true,
                },
            });

            await audioContext.current.audioWorklet.addModule(getWorkletURL());

            const source = audioContext.current.createMediaStreamSource(stream.current);
            micWorkletNode.current = new AudioWorkletNode(audioContext.current, 'recorder-worklet-processor');
            source.connect(micWorkletNode.current);
            const pcmBuffer: any = [];
            micWorkletNode.current.port.onmessage = (event) => {
                const rawPcm = event.data;

                recordedChunks.current.push(new Float32Array(rawPcm));
                pcmBuffer.push(...floatToPCM16(new Float32Array(rawPcm)));
                while (pcmBuffer.length >= 800) {
                    const chunk = pcmBuffer.splice(0, 800);
                    pcmBuffer.splice(0, 800);
                    if (wsRef?.current?.readyState === WebSocket.OPEN) {
                        wsRef.current.send(new Int16Array(chunk).buffer);
                    }
                }

                if (workerRef.current) {
                    workerRef.current.postMessage(
                        {
                            type: 'process',
                            data: {
                                pcmData: rawPcm,
                                isVadActive: true,
                            },
                        },
                        [rawPcm.buffer],
                    );
                }
            };

            // Worker handler
            if (workerRef.current) {
                workerRef.current.onmessage = (event) => {
                    const { type, data, error } = event.data;
                    if (type === 'silero-processed') {
                        setIsSpeaking(data);
                    }
                    if (type === 'error') {
                        console.error('Worker error:', error);
                    }
                };
            }
        } catch (Error) {
            console.log(Error);
        }
    }, [ready, resetSileroStates, workerRef, selectedMic, isRecordingStarted, rules.silenceBeforeRetry]);

    const onStop = async (success?: boolean) => {
        const merged = mergeChunks(recordedChunks.current);
        const blob = encodeToWav(merged);

        speakTimer.current && clearTimeout(speakTimer.current)

        if (success) {
            uploadAudio(blob);
        }

        setIsRecordingStarted(false);
        const end = Date.now();
        preCheckEndTimestampRef.current = new Date().toISOString();
        const elapsed = end - startTime.current;
        recordingTime.current = formatDuration(elapsed, true) as number;

        stream.current?.getTracks().forEach((track) => track.stop());
        stream.current = null;

        micWorkletNode.current?.disconnect();
        micWorkletNode.current = null;

        audioContext.current?.close();
        audioContext.current = null;
        recordedChunks.current = [];
        resetSileroStates();

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'stopTranscription' }));
        }
    };

    const uploadAudio = async (blob: Blob) => {
        try {
            const fileFormat = '.wav';
            const fileName = 'speaking_test';

            const response = await uploadAudioFile(blob, fileFormat, fileName, testTakerDetails, insperaDetails);

            if (response) {
                props.setIsSpeakCheckFinished(true);
                dispatch(
                    changeSpeakPrecheckDuration({
                        duration: recordingTime.current,
                        responseFilePath: response.data.filePath,
                    }),
                );

                dispatch(
                    changePageDetails({
                        ...pageObject,
                        isSpeakCheckDone: true,
                    }),
                );

                setUploadingAudio(false);
            } else {
                dispatch(changePrecheckApiError('Error during upload Api'));
            }
        } catch (error) {
            console.error('Error uploading audio:', error);
        }
    };

    return (
        <>
            {errorScreen ? (
                <>
                    <p className={classes.topRight}>
                        <Text text={`V207`} fontWeight="medium" variant="body" />
                    </p>
                    <p>
                        <Text text="We can't hear you." />
                    </p>
                    <p>
                        <Text text="This may be because you are speaking too quietly, or because there is too much noise around you." />
                    </p>
                    <p>
                        <Text text="Please move closer to the microphone or try speaking more loudly." />
                    </p>

                    <Button
                        size={getButtonSize(fontSize)}
                        onClick={() => {
                            setErrorScreen(false);
                            setIsRecordingStarted(false);
                        }}
                    >
                        Try Again
                    </Button>
                </>
            ) : (
                <>
                    <p>
                        <Text text="We need to check your microphone." />
                    </p>
                    <p>
                        <Text text="Click <b>Record</b> and read the sentence below." />
                    </p>

                    <Button
                        size={getButtonSize(fontSize)}
                        onClick={onStart}
                        ref={elementRef}
                        locked={isRecordingStarted}
                        id="record"
                        loading={uploadingAudio || !rules.samplePhrase}
                    >
                        {isRecordingStarted && (
                            <LiveAudioVisualizerV2 pcmChunks={recordedChunks.current} barColor={computedStyles.color} />
                        )}

                        {uploadingAudio && !isRecordingStarted && 'Uploading'}
                        {!uploadingAudio && !isRecordingStarted && 'Record'}
                    </Button>

                    <p className={classes.sentence}>
                        <Text text={`"${rules.samplePhrase}"`} variant="heading" fontWeight="semibold" />
                    </p>
                </>
            )}
        </>
    );
};

export default SpeakCheckScreen;
